import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, ChevronLeft, LogOut, Upload, Download, FileSpreadsheet, ImagePlus, Package } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import ReviewsTab from "@/components/admin/ReviewsTab";
import InquiriesTab from "@/components/admin/InquiriesTab";
import PartnersTab from "@/components/admin/PartnersTab";
import AdminUsersTab from "@/components/admin/AdminUsersTab";
import OffersTab from "@/components/admin/OffersTab";

const API = `/api`;

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Product Groups & Variants
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [variants, setVariants] = useState([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", slug: "", description: "", image_url: "" });
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [variantForm, setVariantForm] = useState({
    color_name: "", hex_code: "#000000", image_url: "", lot_no: "", stock_rolls: 0,
    discount_percent: "", discount_label: ""
  });
  const [colorMode, setColorMode] = useState("picker");
  const [imageUploading, setImageUploading] = useState(false);
  const [groupImageUploading, setGroupImageUploading] = useState(false);

  // Other tabs
  const [inquiries, setInquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [partners, setPartners] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [viewingInquiry, setViewingInquiry] = useState(null);
  const [viewingPartner, setViewingPartner] = useState(null);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "" });
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerForm, setOfferForm] = useState({ title: "", description: "", discount_text: "", valid_until: "", is_active: true });

  // ===== Data fetching =====
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [groupsRes, inqRes, revRes, partRes, adminRes, offRes] = await Promise.all([
        axios.get(`${API}/groups`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/partners`),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/offers`)
      ]);
      setGroups(groupsRes.data);
      setInquiries(inqRes.data);
      setReviews(revRes.data);
      setPartners(partRes.data);
      setAdminUsers(adminRes.data);
      setOffers(offRes.data);
    } catch (error) {
      toast.error("Error: " + (error.response?.data?.detail || "Something went wrong"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchVariants = useCallback(async (groupId) => {
    try {
      const res = await axios.get(`${API}/variants?group_id=${groupId}`);
      setVariants(res.data);
    } catch { setVariants([]); }
  }, []);

  useEffect(() => {
    if (selectedGroup) fetchVariants(selectedGroup.id);
    else setVariants([]);
  }, [selectedGroup, fetchVariants]);

  // ===== Image upload helper =====
  const uploadImage = async (file) => {
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) { toast.error("Only JPEG, PNG, WebP, GIF allowed"); return null; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/image`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      return res.data.url;
    } catch { toast.error("Upload failed"); return null; }
  };

  // ===== Group handlers =====
  const resetGroupForm = () => { setEditingGroup(null); setGroupForm({ name: "", slug: "", description: "", image_url: "" }); };
  
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGroup) {
        await axios.put(`${API}/groups/${editingGroup.id}`, groupForm, { withCredentials: true });
        toast.success("Group updated");
      } else {
        const slug = groupForm.slug || groupForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        await axios.post(`${API}/groups`, { ...groupForm, slug }, { withCredentials: true });
        toast.success("Group created");
      }
      setGroupDialogOpen(false);
      resetGroupForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save group"); }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, slug: group.slug, description: group.description || "", image_url: group.image_url || "" });
    setGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("Delete this group and ALL its colour variants?")) return;
    try {
      await axios.delete(`${API}/groups/${id}`, { withCredentials: true });
      toast.success("Group deleted");
      if (selectedGroup?.id === id) setSelectedGroup(null);
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleGroupImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGroupImageUploading(true);
    const url = await uploadImage(file);
    if (url) setGroupForm(f => ({ ...f, image_url: url }));
    setGroupImageUploading(false);
  };

  // ===== Variant handlers =====
  const resetVariantForm = () => {
    setEditingVariant(null);
    setVariantForm({ color_name: "", hex_code: "#000000", image_url: "", lot_no: "", stock_rolls: 0, discount_percent: "", discount_label: "" });
    setColorMode("picker");
  };

  const handleVariantSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGroup) return;
    try {
      const payload = {
        ...variantForm,
        group_id: selectedGroup.id,
        stock_rolls: parseInt(variantForm.stock_rolls) || 0,
        discount_percent: variantForm.discount_percent ? parseFloat(variantForm.discount_percent) : null,
        discount_label: variantForm.discount_label || null,
      };
      if (editingVariant) {
        const { group_id, ...updateData } = payload;
        await axios.put(`${API}/variants/${editingVariant.id}`, updateData, { withCredentials: true });
        toast.success("Variant updated");
      } else {
        await axios.post(`${API}/variants`, payload, { withCredentials: true });
        toast.success("Variant created");
      }
      setVariantDialogOpen(false);
      resetVariantForm();
      fetchVariants(selectedGroup.id);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save variant"); }
  };

  const handleEditVariant = (v) => {
    setEditingVariant(v);
    setVariantForm({
      color_name: v.color_name, hex_code: v.hex_code || "#000000",
      image_url: v.image_url || "", lot_no: v.lot_no || "",
      stock_rolls: v.stock_rolls || 0,
      discount_percent: v.discount_percent || "", discount_label: v.discount_label || ""
    });
    setColorMode(v.image_url ? "image" : "picker");
    setVariantDialogOpen(true);
  };

  const handleDeleteVariant = async (id) => {
    if (!window.confirm("Delete this colour variant?")) return;
    try {
      await axios.delete(`${API}/variants/${id}`, { withCredentials: true });
      toast.success("Variant deleted");
      fetchVariants(selectedGroup.id);
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleVariantImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    const url = await uploadImage(file);
    if (url) setVariantForm(f => ({ ...f, image_url: url }));
    setImageUploading(false);
  };

  // ===== Excel handlers =====
  const handleExportVariants = async () => {
    try {
      const res = await axios.get(`${API}/variants/export/excel`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `dugars_variants_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/variants/template/excel`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'variant_import_template.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Template downloaded");
    } catch { toast.error("Download failed"); }
  };

  const handleImportVariants = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await axios.post(`${API}/variants/import/excel`, fd, {
        withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(res.data.message);
      if (res.data.errors?.length) toast.error(`${res.data.errors.length} errors`);
      fetchData();
      if (selectedGroup) fetchVariants(selectedGroup.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = '';
  };

  // ===== Admin user handlers =====
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdmin) {
        await axios.put(`${API}/admin/users/${editingAdmin.id}`, { name: adminForm.name, email: adminForm.email }, { withCredentials: true });
        toast.success("Admin updated");
      } else {
        await axios.post(`${API}/admin/users`, adminForm, { withCredentials: true });
        toast.success("Admin created");
      }
      setAdminDialogOpen(false);
      setEditingAdmin(null);
      setAdminForm({ name: "", email: "", password: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${resetPasswordUser.id}/password`, { password: newPassword }, { withCredentials: true });
      toast.success("Password reset");
      setResetPasswordDialogOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm("Delete this admin?")) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true });
      toast.success("Admin deleted"); fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  // ===== Offer handlers =====
  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOffer) {
        await axios.put(`${API}/offers/${editingOffer.id}`, offerForm, { withCredentials: true });
        toast.success("Offer updated");
      } else {
        await axios.post(`${API}/offers`, offerForm, { withCredentials: true });
        toast.success("Offer created");
      }
      setOfferDialogOpen(false); setEditingOffer(null);
      setOfferForm({ title: "", description: "", discount_text: "", valid_until: "", is_active: true });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try { await axios.delete(`${API}/offers/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchData(); }
    catch { toast.error("Failed"); }
  };

  const handleToggleOfferActive = async (offer) => {
    try {
      await axios.put(`${API}/offers/${offer.id}`, { is_active: !offer.is_active }, { withCredentials: true });
      toast.success(offer.is_active ? "Deactivated" : "Activated"); fetchData();
    } catch { toast.error("Failed"); }
  };

  // ===== Status update & logout =====
  const updateStatus = async (collection, id, status) => {
    try {
      await axios.put(`${API}/${collection}/${id}/status`, { status }, { withCredentials: true });
      toast.success("Status updated"); fetchData();
    } catch { toast.error("Failed"); }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div>;

  const stockConfig = {
    in_stock: { bg: "bg-green-100", text: "text-green-800", label: "In Stock" },
    limited: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Limited" },
    out_of_stock: { bg: "bg-red-100", text: "text-red-800", label: "Out of Stock" }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-[#1C1C1A]">Admin Panel</h1>
            <p className="text-sm text-[#6B6A65] mt-1">Welcome, {user?.name || user?.email}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-sm text-[#6B6A65] hover:text-red-600 border border-[#E6E4DD] hover:border-red-200 transition-colors" data-testid="logout-btn">
            <LogOut size={16} /> Logout
          </button>
        </div>

        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-white border border-[#E6E4DD] p-1 flex flex-wrap">
            <TabsTrigger value="products" className="data-[state=active]:bg-[#1C1C1A] data-[state=active]:text-white px-6" data-testid="tab-products">Products ({groups.length})</TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-[#1C1C1A] data-[state=active]:text-white px-6" data-testid="tab-reviews">Reviews ({reviews.length})</TabsTrigger>
            <TabsTrigger value="inquiries" className="data-[state=active]:bg-[#1C1C1A] data-[state=active]:text-white px-6" data-testid="tab-inquiries">Inquiries ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-[#1C1C1A] data-[state=active]:text-white px-6" data-testid="tab-partners">Partners ({partners.length})</TabsTrigger>
            <TabsTrigger value="admins" className="data-[state=active]:bg-[#1C1C1A] data-[state=active]:text-white px-6" data-testid="tab-admins">Admin Users ({adminUsers.length})</TabsTrigger>
            <TabsTrigger value="offers" className="data-[state=active]:bg-[#1C1C1A] data-[state=active]:text-white px-6" data-testid="tab-offers">Offers ({offers.length})</TabsTrigger>
          </TabsList>

          {/* ======== PRODUCTS TAB (inline - core functionality) ======== */}
          <TabsContent value="products">
            <div className="bg-white border border-[#E6E4DD]">
              {!selectedGroup ? (
                <>
                  <div className="p-6 border-b border-[#E6E4DD] flex flex-wrap justify-between items-center gap-4">
                    <div>
                      <h2 className="font-medium text-[#1C1C1A]">Product Groups</h2>
                      <p className="text-sm text-[#6B6A65]">Manage fabric collections and their colour variants</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={handleDownloadTemplate} className="btn-secondary text-xs py-2 px-3 flex items-center gap-1" data-testid="template-btn"><Download size={14} /> Template</button>
                      <button onClick={handleExportVariants} className="btn-secondary text-xs py-2 px-3 flex items-center gap-1" data-testid="export-btn"><FileSpreadsheet size={14} /> Export</button>
                      <label className="btn-secondary text-xs py-2 px-3 flex items-center gap-1 cursor-pointer" data-testid="import-btn">
                        <Upload size={14} /> Import Excel
                        <input type="file" accept=".xlsx,.xls" onChange={handleImportVariants} className="hidden" />
                      </label>
                      <button onClick={() => { resetGroupForm(); setGroupDialogOpen(true); }} className="btn-primary text-xs py-2 px-4 flex items-center gap-1" data-testid="add-group-btn"><Plus size={14} /> Add Group</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="admin-table">
                      <thead><tr><th>Group</th><th>Slug</th><th>Colours</th><th>Actions</th></tr></thead>
                      <tbody>
                        {groups.map(g => (
                          <tr key={g.id} data-testid={`group-row-${g.id}`}>
                            <td>
                              <div className="flex items-center gap-3">
                                {g.image_url ? <img src={g.image_url} alt="" className="w-10 h-10 object-cover border border-[#E6E4DD]" /> : <div className="w-10 h-10 bg-[#F7F5F0] flex items-center justify-center"><Package size={16} className="text-[#6B6A65]" /></div>}
                                <div>
                                  <p className="font-medium text-[#1C1C1A]">{g.name}</p>
                                  {g.description && <p className="text-xs text-[#6B6A65] line-clamp-1">{g.description}</p>}
                                </div>
                              </div>
                            </td>
                            <td className="text-sm text-[#6B6A65]">{g.slug}</td>
                            <td>
                              <button onClick={() => setSelectedGroup(g)} className="px-3 py-1 text-xs bg-[#4A5D4E]/10 text-[#4A5D4E] hover:bg-[#4A5D4E]/20 transition-colors" data-testid={`view-variants-${g.id}`}>{g.variant_count || 0} Colours</button>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <button onClick={() => setSelectedGroup(g)} className="p-2 hover:bg-[#F7F5F0]" title="View Variants"><Eye size={16} className="text-[#6B6A65]" /></button>
                                <button onClick={() => handleEditGroup(g)} className="p-2 hover:bg-[#F7F5F0]" title="Edit" data-testid={`edit-group-${g.id}`}><Pencil size={16} className="text-[#6B6A65]" /></button>
                                <button onClick={() => handleDeleteGroup(g.id)} className="p-2 hover:bg-red-50" title="Delete" data-testid={`delete-group-${g.id}`}><Trash2 size={16} className="text-red-500" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {groups.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-[#6B6A65]">No product groups yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-6 border-b border-[#E6E4DD] flex flex-wrap justify-between items-center gap-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setSelectedGroup(null)} className="p-2 hover:bg-[#F7F5F0] transition-colors" data-testid="back-to-groups"><ChevronLeft size={20} /></button>
                      <div>
                        <h2 className="font-medium text-[#1C1C1A]">{selectedGroup.name}</h2>
                        <p className="text-sm text-[#6B6A65]">{variants.length} colour variants</p>
                      </div>
                    </div>
                    <button onClick={() => { resetVariantForm(); setVariantDialogOpen(true); }} className="btn-primary text-xs py-2 px-4 flex items-center gap-1" data-testid="add-variant-btn"><Plus size={14} /> Add Colour</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="admin-table">
                      <thead><tr><th>Colour</th><th>Lot No.</th><th>Stock (Rolls)</th><th>Status</th><th>Discount</th><th>Actions</th></tr></thead>
                      <tbody>
                        {variants.map(v => {
                          const sc = stockConfig[v.stock_status] || stockConfig.in_stock;
                          return (
                            <tr key={v.id} data-testid={`variant-row-${v.id}`}>
                              <td>
                                <div className="flex items-center gap-2">
                                  {v.image_url ? <img src={v.image_url} alt="" className="w-8 h-8 object-cover border" /> : <div className="w-8 h-8 border border-[#E6E4DD]" style={{ backgroundColor: v.hex_code || '#ccc' }} />}
                                  <span className="text-[#1C1C1A]">{v.color_name}</span>
                                </div>
                              </td>
                              <td className="text-sm text-[#6B6A65]">{v.lot_no || '-'}</td>
                              <td className="text-sm font-medium">{v.stock_rolls} <span className="text-[#6B6A65] font-normal">({v.stock_rolls * 30} kg)</span></td>
                              <td><span className={`px-2 py-0.5 text-xs ${sc.bg} ${sc.text}`}>{sc.label}</span></td>
                              <td>
                                {(v.discount_percent || v.discount_label) ? (
                                  <span className="px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700">{v.discount_percent ? `${v.discount_percent}%` : v.discount_label}</span>
                                ) : '-'}
                              </td>
                              <td>
                                <div className="flex items-center gap-2">
                                  <button onClick={() => handleEditVariant(v)} className="p-2 hover:bg-[#F7F5F0]" data-testid={`edit-variant-${v.id}`}><Pencil size={16} className="text-[#6B6A65]" /></button>
                                  <button onClick={() => handleDeleteVariant(v.id)} className="p-2 hover:bg-red-50" data-testid={`delete-variant-${v.id}`}><Trash2 size={16} className="text-red-500" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {variants.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-[#6B6A65]">No colour variants. Click "Add Colour" to start.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="reviews"><ReviewsTab reviews={reviews} updateStatus={updateStatus} /></TabsContent>
          <TabsContent value="inquiries"><InquiriesTab inquiries={inquiries} updateStatus={updateStatus} setViewingInquiry={setViewingInquiry} /></TabsContent>
          <TabsContent value="partners"><PartnersTab partners={partners} updateStatus={updateStatus} setViewingPartner={setViewingPartner} /></TabsContent>
          <TabsContent value="admins">
            <AdminUsersTab
              adminUsers={adminUsers}
              onAdd={() => { setEditingAdmin(null); setAdminForm({ name: "", email: "", password: "" }); setAdminDialogOpen(true); }}
              onEdit={(a) => { setEditingAdmin(a); setAdminForm({ name: a.name, email: a.email, password: "" }); setAdminDialogOpen(true); }}
              onResetPassword={(a) => { setResetPasswordUser(a); setNewPassword(""); setResetPasswordDialogOpen(true); }}
              onDelete={handleDeleteAdmin}
            />
          </TabsContent>
          <TabsContent value="offers">
            <OffersTab
              offers={offers}
              onAdd={() => { setEditingOffer(null); setOfferForm({ title: "", description: "", discount_text: "", valid_until: "", is_active: true }); setOfferDialogOpen(true); }}
              onEdit={(o) => { setEditingOffer(o); setOfferForm({ title: o.title, description: o.description, discount_text: o.discount_text, valid_until: o.valid_until || "", is_active: o.is_active }); setOfferDialogOpen(true); }}
              onDelete={handleDeleteOffer}
              onToggleActive={handleToggleOfferActive}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* ======== DIALOGS ======== */}

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Cormorant_Garamond'] text-2xl">{editingGroup ? "Edit Group" : "Add Product Group"}</DialogTitle></DialogHeader>
          <form onSubmit={handleGroupSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Group Name *</label>
              <input type="text" value={groupForm.name} onChange={e => { setGroupForm(f => ({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })); }} className="custom-input w-full" required data-testid="group-name-input" placeholder="e.g. 50s NR PLAIN DYED" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <input type="text" value={groupForm.slug} onChange={e => setGroupForm(f => ({ ...f, slug: e.target.value }))} className="custom-input w-full" data-testid="group-slug-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea value={groupForm.description} onChange={e => setGroupForm(f => ({ ...f, description: e.target.value }))} className="custom-input w-full" rows={2} data-testid="group-desc-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Group Image</label>
              <div className="flex gap-2 items-center">
                <label className={`px-3 py-2 text-xs border border-[#E6E4DD] cursor-pointer hover:bg-[#F7F5F0] flex items-center gap-1 ${groupImageUploading ? 'opacity-50' : ''}`}>
                  <ImagePlus size={14} /> {groupImageUploading ? 'Uploading...' : 'Upload'}
                  <input type="file" accept="image/*" onChange={handleGroupImageUpload} className="hidden" disabled={groupImageUploading} />
                </label>
                {groupForm.image_url && <img src={groupForm.image_url} alt="" className="w-10 h-10 object-cover border" />}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setGroupDialogOpen(false)} className="btn-secondary py-2 px-6">Cancel</button>
              <button type="submit" className="btn-primary py-2 px-6" data-testid="save-group-btn">{editingGroup ? "Update" : "Create"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Variant Dialog */}
      <Dialog open={variantDialogOpen} onOpenChange={setVariantDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Cormorant_Garamond'] text-2xl">{editingVariant ? "Edit Colour" : "Add Colour Variant"}</DialogTitle></DialogHeader>
          <form onSubmit={handleVariantSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Colour Name *</label>
              <input type="text" value={variantForm.color_name} onChange={e => setVariantForm(f => ({ ...f, color_name: e.target.value }))} className="custom-input w-full" required data-testid="variant-color-input" placeholder="e.g. Navy Blue" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium">Colour Display</label>
                <div className="flex bg-[#F7F5F0] border border-[#E6E4DD] p-0.5 text-xs">
                  <button type="button" onClick={() => setColorMode("picker")} className={`px-3 py-1 ${colorMode === "picker" ? "bg-[#1C1C1A] text-white" : "text-[#6B6A65]"}`} data-testid="color-mode-picker">Colour Picker</button>
                  <button type="button" onClick={() => setColorMode("image")} className={`px-3 py-1 ${colorMode === "image" ? "bg-[#1C1C1A] text-white" : "text-[#6B6A65]"}`} data-testid="color-mode-image">Upload Image</button>
                </div>
              </div>
              {colorMode === "picker" ? (
                <div className="flex items-center gap-3">
                  <input type="color" value={variantForm.hex_code} onChange={e => setVariantForm(f => ({ ...f, hex_code: e.target.value }))} className="w-12 h-10 border cursor-pointer" data-testid="variant-hex-input" />
                  <input type="text" value={variantForm.hex_code} onChange={e => setVariantForm(f => ({ ...f, hex_code: e.target.value }))} className="custom-input flex-1" placeholder="#000000" />
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  <label className={`px-3 py-2 text-xs border border-[#E6E4DD] cursor-pointer hover:bg-[#F7F5F0] flex items-center gap-1 ${imageUploading ? 'opacity-50' : ''}`}>
                    <ImagePlus size={14} /> {imageUploading ? 'Uploading...' : 'Upload Swatch'}
                    <input type="file" accept="image/*" onChange={handleVariantImageUpload} className="hidden" disabled={imageUploading} data-testid="variant-image-upload" />
                  </label>
                  {variantForm.image_url && <img src={variantForm.image_url} alt="" className="w-10 h-10 object-cover border" />}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Lot No.</label>
                <input type="text" value={variantForm.lot_no} onChange={e => setVariantForm(f => ({ ...f, lot_no: e.target.value }))} className="custom-input w-full" data-testid="variant-lot-input" placeholder="LOT-001" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stock (Rolls)</label>
                <input type="number" min="0" value={variantForm.stock_rolls} onChange={e => setVariantForm(f => ({ ...f, stock_rolls: e.target.value }))} className="custom-input w-full" data-testid="variant-stock-input" />
                <p className="text-xs text-[#6B6A65] mt-0.5">1 roll = 30 kg</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Discount %</label>
                <input type="number" min="0" max="100" step="0.1" value={variantForm.discount_percent} onChange={e => setVariantForm(f => ({ ...f, discount_percent: e.target.value }))} className="custom-input w-full" data-testid="variant-discount-input" placeholder="e.g. 10" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Discount Label</label>
                <input type="text" value={variantForm.discount_label} onChange={e => setVariantForm(f => ({ ...f, discount_label: e.target.value }))} className="custom-input w-full" data-testid="variant-label-input" placeholder="SALE, CLEARANCE" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setVariantDialogOpen(false)} className="btn-secondary py-2 px-6">Cancel</button>
              <button type="submit" className="btn-primary py-2 px-6" data-testid="save-variant-btn">{editingVariant ? "Update" : "Add"} Colour</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inquiry Detail Dialog */}
      <Dialog open={!!viewingInquiry} onOpenChange={() => setViewingInquiry(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Inquiry Details</DialogTitle></DialogHeader>
          {viewingInquiry && (
            <div className="space-y-3 mt-4 text-sm">
              <p><strong>Name:</strong> {viewingInquiry.name}</p>
              <p><strong>Email:</strong> {viewingInquiry.email}</p>
              <p><strong>Phone:</strong> {viewingInquiry.phone}</p>
              {viewingInquiry.company && <p><strong>Company:</strong> {viewingInquiry.company}</p>}
              <p><strong>Message:</strong> {viewingInquiry.message}</p>
              {viewingInquiry.selected_products?.length > 0 && (
                <div><strong>Products:</strong>{viewingInquiry.selected_products.map((sp) => <span key={sp.product_name} className="ml-2 px-2 py-1 bg-[#F7F5F0] text-xs">{sp.product_name}</span>)}</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Partner Detail Dialog */}
      <Dialog open={!!viewingPartner} onOpenChange={() => setViewingPartner(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Partner Application</DialogTitle></DialogHeader>
          {viewingPartner && (
            <div className="space-y-3 mt-4 text-sm">
              <p><strong>Business:</strong> {viewingPartner.business_name}</p>
              <p><strong>Contact:</strong> {viewingPartner.contact_person}</p>
              <p><strong>Email:</strong> {viewingPartner.email}</p>
              <p><strong>Phone:</strong> {viewingPartner.phone}</p>
              {viewingPartner.business_type && <p><strong>Type:</strong> {viewingPartner.business_type}</p>}
              {viewingPartner.monthly_requirement && <p><strong>Monthly Req:</strong> {viewingPartner.monthly_requirement}</p>}
              {viewingPartner.message && <p><strong>Message:</strong> {viewingPartner.message}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin User Dialog */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingAdmin ? "Edit Admin" : "Add Admin User"}</DialogTitle></DialogHeader>
          <form onSubmit={handleAdminSubmit} className="space-y-4 mt-4">
            <div><label className="block text-sm font-medium mb-1">Name</label><input type="text" value={adminForm.name} onChange={e => setAdminForm(f => ({ ...f, name: e.target.value }))} className="custom-input w-full" required /></div>
            <div><label className="block text-sm font-medium mb-1">Email</label><input type="email" value={adminForm.email} onChange={e => setAdminForm(f => ({ ...f, email: e.target.value }))} className="custom-input w-full" required /></div>
            {!editingAdmin && <div><label className="block text-sm font-medium mb-1">Password</label><input type="password" value={adminForm.password} onChange={e => setAdminForm(f => ({ ...f, password: e.target.value }))} className="custom-input w-full" required /></div>}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setAdminDialogOpen(false)} className="btn-secondary py-2 px-6">Cancel</button>
              <button type="submit" className="btn-primary py-2 px-6">{editingAdmin ? "Update" : "Create"}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password for {resetPasswordUser?.email}</DialogTitle></DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <div><label className="block text-sm font-medium mb-1">New Password</label><input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="custom-input w-full" required minLength={6} /></div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setResetPasswordDialogOpen(false)} className="btn-secondary py-2 px-6">Cancel</button>
              <button type="submit" className="btn-primary py-2 px-6">Reset Password</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Offer Dialog */}
      <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-['Cormorant_Garamond'] text-2xl">{editingOffer ? "Edit Offer" : "Add New Offer"}</DialogTitle></DialogHeader>
          <form onSubmit={handleOfferSubmit} className="space-y-4 mt-4">
            <div><label className="block text-sm font-medium mb-1">Title *</label><input type="text" value={offerForm.title} onChange={e => setOfferForm(f => ({ ...f, title: e.target.value }))} className="custom-input w-full" required data-testid="offer-title-input" /></div>
            <div><label className="block text-sm font-medium mb-1">Description *</label><textarea value={offerForm.description} onChange={e => setOfferForm(f => ({ ...f, description: e.target.value }))} className="custom-input w-full" rows={2} required data-testid="offer-description-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium mb-1">Discount Label *</label><input type="text" value={offerForm.discount_text} onChange={e => setOfferForm(f => ({ ...f, discount_text: e.target.value }))} className="custom-input w-full" required data-testid="offer-discount-input" /></div>
              <div><label className="block text-sm font-medium mb-1">Valid Until</label><input type="text" value={offerForm.valid_until} onChange={e => setOfferForm(f => ({ ...f, valid_until: e.target.value }))} className="custom-input w-full" data-testid="offer-valid-input" /></div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={offerForm.is_active} onChange={e => setOfferForm(f => ({ ...f, is_active: e.target.checked }))} id="offer-active" className="w-4 h-4" />
              <label htmlFor="offer-active" className="text-sm">Active (visible on Collections page)</label>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button type="button" onClick={() => setOfferDialogOpen(false)} className="btn-secondary py-2 px-6">Cancel</button>
              <button type="submit" className="btn-primary py-2 px-6" data-testid="save-offer-btn">{editingOffer ? "Update" : "Create"} Offer</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
